import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CentralWhatsappCredentialsView, CentralWhatsappNumberDetailView, CentralWhatsappNumberView } from '@vello/api-client';
import {
  Badge, Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, EmptyState, Field, FilterBar, ListTable, Notice, PageHeader, Row, SectionTitle, Select, Stack, Text, TextArea, TextInput, type Column, type TimelineEntry,
} from '@vello/web-theme';
import {
  CREDENTIAL_KIND_ORDER, NUMBER_STATUS_CHOICES, NUMBER_QUALITY_CHOICES, TEMPLATE_CATEGORY_CHOICES, centralWhatsappAuthority, createIssue, credentialsComplete, draftFromNumber, numberDraftPatch, editIssue, emptyTemplate,
  formatMarketInstant, numberHasChanges, newNumberDraft, numberCountries, numberDisplayName, numberMarketsOfCountry, numberStatusTone, qualityTone, registerInput, removeTemplate, replaceTemplate, statusActions, templateParamCount,
  type NumberDraft, type StatusAction, type TemplateDraft,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { useCountryName } from '../i18n/country-name';
import { DrawerStack } from '../components/drawer-stack';
import { HistoryTimeline } from '../components/history-timeline';

/**
 * Operações › Números de WhatsApp (D636, doc 25 phase 1, owner Q-365: Meta's Cloud API, direct): the business numbers the Central talks to
 * customers through. The list shows a number per row (the name and the number a customer sees, the country - and city - it serves, its status, its
 * WhatsApp quality and how many templates are approved); a row opens the number in a drawer on the right (D619) where an administrator edits it under
 * a compare-and-set version, sets its three secrets WRITE-ONLY (a configured badge and a masked hint, never a value), registers the approved
 * templates the desk may send outside the 24 h window, and walks it PENDING -> ACTIVE <-> PAUSED -> RETIRED. Every code is a word through the
 * vocabulary catalog; layout and type come from web-theme (no inline style).
 */

type Msg = { readonly tone: 'success' | 'danger'; readonly text: string };
type StatusFilter = '' | 'PENDING' | 'ACTIVE' | 'PAUSED' | 'RETIRED';
type SecretInputs = Readonly<Record<'access_token' | 'app_secret' | 'verify_token', string>>;
const NO_SECRETS: SecretInputs = { access_token: '', app_secret: '', verify_token: '' };
/** The kind of each secret and the field of the request that carries it. */
const SECRET_FIELDS = [['access_token', 'accessToken'], ['app_secret', 'appSecret'], ['verify_token', 'verifyToken']] as const;
const fieldOf = (kind: keyof SecretInputs): string => SECRET_FIELDS.find(([k]) => k === kind)?.[1] ?? kind;

export function CentralWhatsappScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { t, lang } = useT();
  const { v, explain, fail } = useVocab();
  const { confirm } = useConsole();
  const countryName = useCountryName();
  const authority = centralWhatsappAuthority(grants);

  const [numbers, setNumbers] = useState<readonly CentralWhatsappNumberView[] | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [country, setCountry] = useState('');
  const [status, setStatus] = useState<StatusFilter>('');
  const [notice, setNotice] = useState<Msg | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState<CentralWhatsappNumberDetailView | undefined>(undefined);
  const [draft, setDraft] = useState<NumberDraft | undefined>(undefined);
  const [creating, setCreating] = useState<NumberDraft | undefined>(undefined);
  const [secrets, setSecrets] = useState<SecretInputs>(NO_SECRETS);
  const readSeq = useRef(0);

  const refuse = useCallback(
    (e: { readonly code: string; readonly reasonCode?: string | undefined; readonly params?: Readonly<Record<string, string | number>> | undefined }): string => explain(e) ?? t('lmap.err.generic', { code: fail(e) }),
    [explain, fail, t],
  );

  const load = useCallback(async (): Promise<void> => {
    const seq = ++readSeq.current;
    const res = await runtime.centralWhatsapp.list({ ...(country !== '' ? { country } : {}), ...(status !== '' ? { status } : {}) });
    if (seq !== readSeq.current) return;
    if (res.ok) {
      setNumbers(res.value.numbers);
      setError(undefined);
    } else {
      // A refused read keeps the list unknown: the screen says it could not load, never "no numbers".
      setError(refuse(res.error));
    }
  }, [runtime, country, status, refuse]);

  useEffect(() => {
    void load();
  }, [load]);

  const openNumber = useCallback(
    async (numberId: string): Promise<void> => {
      setCreating(undefined);
      const res = await runtime.centralWhatsapp.get(numberId);
      if (res.ok) {
        setDetail(res.value);
        setDraft(draftFromNumber(res.value.number));
        setSecrets(NO_SECRETS);
      } else {
        setNotice({ tone: 'danger', text: refuse(res.error) });
      }
    },
    [runtime, refuse],
  );

  /** Re-read the open number and the list after a change (the version moved). */
  const refreshOpen = useCallback(
    async (numberId: string): Promise<void> => {
      const res = await runtime.centralWhatsapp.get(numberId);
      if (res.ok) {
        setDetail(res.value);
        setDraft(draftFromNumber(res.value.number));
      }
      await load();
    },
    [runtime, load],
  );

  const closeDrawer = (): void => {
    setDetail(undefined);
    setDraft(undefined);
    setCreating(undefined);
    setSecrets(NO_SECRETS);
  };

  const nameOf = (n: CentralWhatsappNumberView): string => numberDisplayName(n);
  const marketName = (id: string | undefined): string => markets.find((m) => m.id === id)?.name ?? id ?? '';
  const countries = useMemo(() => numberCountries(markets, numbers ?? []), [markets, numbers]);
  const countrySummary = useMemo(() => new Set((numbers ?? []).map((x) => x.countryCode)).size, [numbers]);

  const columns: readonly Column<CentralWhatsappNumberView>[] = [
    {
      key: 'number',
      header: t('cwa.col.number'),
      width: 'minmax(220px, 1.4fr)',
      render: (n) => (
        <Stack gap={1}>
          <Text as="strong">{nameOf(n)}</Text>
          {n.label !== undefined && n.label !== '' && <Text role="caption" tone="secondary" className="tnum">{n.displayNumber}</Text>}
        </Stack>
      ),
    },
    {
      key: 'where',
      header: t('cwa.col.where'),
      width: 'minmax(200px, 1.2fr)',
      render: (n) => (
        <Text>
          {countryName(n.countryCode)} · {n.marketId === undefined ? t('cwa.scope.country') : t('cwa.scope.market', { market: marketName(n.marketId) })}
        </Text>
      ),
    },
    { key: 'status', header: t('cwa.col.status'), width: 'minmax(150px, 0.9fr)', render: (n) => <Badge tone={numberStatusTone(n.status)}>{v('central_whatsapp.number_status', n.status)}</Badge> },
    { key: 'quality', header: t('cwa.col.quality'), width: 'minmax(150px, 0.9fr)', render: (n) => <Badge tone={qualityTone(n.qualityRating)}>{v('whatsapp.quality_rating', n.qualityRating)}</Badge> },
    {
      key: 'templates',
      header: t('cwa.col.templates'),
      width: 'minmax(140px, 0.8fr)',
      render: (n) => <Text className="tnum">{n.templates.length === 0 ? t('cwa.templates.none') : t('cwa.templates.count', { n: n.templates.length })}</Text>,
    },
  ];

  // ---- writes -----------------------------------------------------------------------------------------------------

  const run = async (action: () => Promise<{ readonly ok: true; readonly done: string } | { readonly ok: false; readonly text: string }>): Promise<boolean> => {
    setBusy(true);
    setNotice(undefined);
    const result = await action();
    setBusy(false);
    setNotice(result.ok ? { tone: 'success', text: result.done } : { tone: 'danger', text: result.text });
    return result.ok;
  };

  const save = async (): Promise<void> => {
    if (detail === undefined || draft === undefined) return;
    const patch = numberDraftPatch(detail.number, draft);
    if (!numberHasChanges(patch)) return;
    await run(async () => {
      const res = await runtime.centralWhatsapp.update(detail.number.numberId, detail.number.version, patch);
      return res.ok ? { ok: true as const, done: t('cwa.done.saved') } : { ok: false as const, text: refuse(res.error) };
    });
    await refreshOpen(detail.number.numberId);
  };

  const saveSecrets = async (): Promise<void> => {
    if (detail === undefined) return;
    const input: Record<string, string> = {};
    for (const kind of CREDENTIAL_KIND_ORDER) {
      const value = secrets[kind as keyof SecretInputs].trim();
      if (value !== '') input[fieldOf(kind as keyof SecretInputs)] = value;
    }
    if (Object.keys(input).length === 0) return;
    const ok = await run(async () => {
      const res = await runtime.centralWhatsapp.setCredentials(detail.number.numberId, input);
      return res.ok ? { ok: true as const, done: t('cwa.done.credentials') } : { ok: false as const, text: refuse(res.error) };
    });
    if (ok) setSecrets(NO_SECRETS);
    await refreshOpen(detail.number.numberId);
  };

  const move = async (action: StatusAction): Promise<void> => {
    if (detail === undefined) return;
    const number = detail.number;
    if (action.confirm) {
      const answer = await confirm({ title: t('cwa.retire.title', { name: nameOf(number) }), body: t('cwa.retire.body'), confirmLabel: t('cwa.retire.confirm'), cancelLabel: t('ui.cancel'), tone: 'danger' });
      if (!answer.confirmed) return;
    }
    await run(async () => {
      const res = await runtime.centralWhatsapp.setStatus(number.numberId, number.version, action.to);
      return res.ok ? { ok: true as const, done: t(`cwa.done.${action.to}`) } : { ok: false as const, text: refuse(res.error) };
    });
    await refreshOpen(number.numberId);
  };

  const startCreate = (): void => {
    setDetail(undefined);
    setDraft(undefined);
    setNotice(undefined);
    setCreating(newNumberDraft(country !== '' ? country : (countries[0] ?? '')));
  };

  const create = async (): Promise<void> => {
    if (creating === undefined) return;
    let createdId: string | undefined;
    const made = await run(async () => {
      const res = await runtime.centralWhatsapp.register(registerInput(creating));
      if (res.ok) createdId = res.value.number.numberId;
      return res.ok ? { ok: true as const, done: t('cwa.done.created') } : { ok: false as const, text: refuse(res.error) };
    });
    await load();
    if (made && createdId !== undefined) await openNumber(createdId);
  };

  // ---- the editor shared by the number drawer and the create drawer --------------------------------------------------

  const renderTemplates = (idPrefix: string, d: NumberDraft, onChange: (next: NumberDraft) => void, canEdit: boolean, badIndex: number | undefined): ReactElement => (
    <Stack gap={3}>
      <SectionTitle>{t('cwa.templates.title')}</SectionTitle>
      <Text role="caption" tone="secondary" as="p">{t('cwa.templates.hint')}</Text>
      {d.templates.length === 0 && <Text role="caption" tone="secondary">{t('cwa.templates.empty')}</Text>}
      {d.templates.map((tpl: TemplateDraft, index) => (
        <Stack key={index} gap={2}>
          {badIndex === index && <Notice tone="danger">{t('cwa.template.badIndex', { n: index + 1 })}</Notice>}
          <Row gap={2} wrap align="end">
            <Field label={t('cwa.template.name')} htmlFor={`${idPrefix}-tpl-${index}-name`} hint={t('cwa.template.name.hint')}>
              <TextInput id={`${idPrefix}-tpl-${index}-name`} value={tpl.name} disabled={!canEdit} autoComplete="off" onChange={(e) => onChange({ ...d, templates: replaceTemplate(d.templates, index, { ...tpl, name: e.target.value }) })} />
            </Field>
            <Field label={t('cwa.template.language')} htmlFor={`${idPrefix}-tpl-${index}-lang`} hint={t('cwa.template.language.hint')}>
              <TextInput id={`${idPrefix}-tpl-${index}-lang`} value={tpl.language} disabled={!canEdit} autoComplete="off" onChange={(e) => onChange({ ...d, templates: replaceTemplate(d.templates, index, { ...tpl, language: e.target.value }) })} />
            </Field>
            <Field label={t('cwa.template.category')} htmlFor={`${idPrefix}-tpl-${index}-cat`}>
              <Select id={`${idPrefix}-tpl-${index}-cat`} value={tpl.category} disabled={!canEdit} onChange={(e) => onChange({ ...d, templates: replaceTemplate(d.templates, index, { ...tpl, category: e.target.value }) })}>
                {TEMPLATE_CATEGORY_CHOICES.map((c) => (
                  <option key={c} value={c}>{v('central_whatsapp.template_category', c)}</option>
                ))}
              </Select>
            </Field>
          </Row>
          <Field label={t('cwa.template.preview')} htmlFor={`${idPrefix}-tpl-${index}-text`} footer={<Text role="caption" tone="secondary" className="tnum">{t('cwa.template.params', { n: templateParamCount(tpl) })}</Text>}>
            <TextArea id={`${idPrefix}-tpl-${index}-text`} value={tpl.preview} disabled={!canEdit} rows={3} onChange={(e) => onChange({ ...d, templates: replaceTemplate(d.templates, index, { ...tpl, preview: e.target.value }) })} />
          </Field>
          {canEdit && <Row gap={2}><Button variant="ghost" aria-label={`${t('cwa.template.remove')} ${index + 1}`} onClick={() => onChange({ ...d, templates: removeTemplate(d.templates, index) })}>{t('cwa.template.remove')}</Button></Row>}
        </Stack>
      ))}
      {canEdit && <Row gap={2}><Button variant="secondary" onClick={() => onChange({ ...d, templates: [...d.templates, emptyTemplate()] })}>{t('cwa.template.add')}</Button></Row>}
    </Stack>
  );

  const renderCommonFields = (idPrefix: string, d: NumberDraft, onChange: (next: NumberDraft) => void, canEdit: boolean, mode: 'create' | 'edit'): ReactElement => {
    const cityChoices = numberMarketsOfCountry(markets, d.countryCode);
    return (
      <>
        <Field label={t('cwa.field.displayNumber')} htmlFor={`${idPrefix}-display`} hint={t('cwa.field.displayNumber.hint')}>
          <TextInput id={`${idPrefix}-display`} className="tnum" value={d.displayNumber} disabled={!canEdit} autoComplete="off" inputMode="tel" onChange={(e) => onChange({ ...d, displayNumber: e.target.value })} />
        </Field>
        <Field label={t('cwa.field.label')} htmlFor={`${idPrefix}-label`} hint={t('cwa.field.label.hint')}>
          <TextInput id={`${idPrefix}-label`} value={d.label} maxLength={80} disabled={!canEdit} onChange={(e) => onChange({ ...d, label: e.target.value })} />
        </Field>
        <Field label={t('cwa.field.wabaId')} htmlFor={`${idPrefix}-waba`} hint={t('cwa.field.wabaId.hint')}>
          <TextInput id={`${idPrefix}-waba`} className="tnum" value={d.wabaId} disabled={!canEdit} autoComplete="off" inputMode="numeric" onChange={(e) => onChange({ ...d, wabaId: e.target.value })} />
        </Field>
        {cityChoices.length > 0 && (
          <Field label={t('cwa.field.market')} htmlFor={`${idPrefix}-market`} hint={t('cwa.field.market.hint')}>
            <Select id={`${idPrefix}-market`} value={d.marketId} disabled={!canEdit} onChange={(e) => onChange({ ...d, marketId: e.target.value })}>
              <option value="">{t('cwa.field.market.whole')}</option>
              {cityChoices.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t('cwa.field.apiVersion')} htmlFor={`${idPrefix}-api`} hint={t('cwa.field.apiVersion.hint')}>
          <TextInput id={`${idPrefix}-api`} className="tnum" value={d.apiVersion} disabled={!canEdit} autoComplete="off" onChange={(e) => onChange({ ...d, apiVersion: e.target.value })} />
        </Field>
        {mode === 'edit' && (
          <Field label={t('cwa.field.qualityRating')} htmlFor={`${idPrefix}-quality`} hint={t('cwa.field.qualityRating.hint')}>
            <Select id={`${idPrefix}-quality`} value={d.qualityRating} disabled={!canEdit} onChange={(e) => onChange({ ...d, qualityRating: e.target.value })}>
              {NUMBER_QUALITY_CHOICES.map((q) => (
                <option key={q} value={q}>{v('whatsapp.quality_rating', q)}</option>
              ))}
            </Select>
          </Field>
        )}
        <Field label={t('cwa.field.optOutKeywords')} htmlFor={`${idPrefix}-keywords`} hint={t('cwa.field.optOutKeywords.hint')}>
          <TextInput id={`${idPrefix}-keywords`} value={d.keywordsText} disabled={!canEdit} autoComplete="off" onChange={(e) => onChange({ ...d, keywordsText: e.target.value })} />
        </Field>
      </>
    );
  };

  // ---- the number drawer ----------------------------------------------------------------------------------------------

  const credentialRow = (creds: CentralWhatsappCredentialsView, kind: string): ReactElement => {
    const c = creds[kind as keyof CentralWhatsappCredentialsView];
    return (
      <Row key={kind} gap={3} wrap align="end">
        <Field label={t('cwa.cred.input', { kind: v('central_whatsapp.credential_kind', kind) })} htmlFor={`cwa-cred-${kind}`}>
          <TextInput
            id={`cwa-cred-${kind}`}
            type="password"
            autoComplete="new-password"
            placeholder={t('cwa.cred.placeholder')}
            value={secrets[kind as keyof SecretInputs]}
            disabled={busy || !authority.mayManage}
            onChange={(e) => setSecrets({ ...secrets, [kind]: e.target.value })}
          />
        </Field>
        {c?.configured === true ? <Badge tone="success">{c.maskedHint !== undefined ? t('cwa.cred.set', { hint: c.maskedHint }) : t('cwa.cred.setNoHint')}</Badge> : <Badge tone="warning">{t('cwa.cred.missing')}</Badge>}
      </Row>
    );
  };

  /** The number's history as a timeline (newest first): retiring is red, pausing is amber. */
  const historyEntries = (d: CentralWhatsappNumberDetailView): readonly TimelineEntry[] =>
    d.events.map((ev) => {
      const changed = ev.detail['changed'];
      const credential = ev.detail['credential'];
      const detail = Array.isArray(changed) && changed.length > 0
        ? t('cwa.history.fields', { fields: (changed as readonly string[]).map((f) => t(`cwa.changed.${f}`)).join(', ') })
        : typeof credential === 'string' ? v('central_whatsapp.credential_kind', credential) : undefined;
      return {
        id: ev.eventId,
        tone: ev.kind === 'NUMBER_RETIRED' ? 'danger' : ev.kind === 'NUMBER_PAUSED' ? 'warning' : 'neutral',
        title: v('central_whatsapp.number_event_kind', ev.kind),
        ...(detail === undefined ? {} : { detail }),
        meta: `${formatMarketInstant(ev.at, undefined, lang)} · ${d.operators[ev.actor] ?? t('cwa.history.unnamed')}`,
      };
    });

  const numberDrawer = (d: CentralWhatsappNumberDetailView, current: NumberDraft): ReactElement => {
    const n = d.number;
    const retired = n.status === 'RETIRED';
    const canEdit = authority.mayManage && !retired;
    const issue = canEdit ? editIssue(n, current) : undefined;
    const patch = numberDraftPatch(n, current);
    const complete = credentialsComplete(d.credentials);
    const actions = authority.mayManage ? statusActions(n.status) : [];
    const typed = Object.values(secrets).some((s) => s.trim() !== '');
    return (
      <>
        <DrawerHeader
          titleId="cwa-title"
          title={t('cwa.drawer.title', { name: nameOf(n) })}
          subtitle={
            <Row gap={2} wrap align="center">
              <Badge tone={numberStatusTone(n.status)}>{v('central_whatsapp.number_status', n.status)}</Badge>
              <Badge tone={qualityTone(n.qualityRating)}>{v('whatsapp.quality_rating', n.qualityRating)}</Badge>
              <Text role="caption" tone="secondary">{t('cwa.drawer.country', { country: countryName(n.countryCode) })}</Text>
            </Row>
          }
          onClose={closeDrawer}
          closeLabel={t('ui.close')}
        />
        <DrawerBody>
          <DrawerStack>
            {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}
            {retired && <Notice tone="neutral">{t('cwa.retiredNote')}</Notice>}
            {n.status === 'PAUSED' && <Notice tone="neutral">{t('cwa.status.pausedNote')}</Notice>}

            <Stack gap={3}>
              <SectionTitle>{t('cwa.status.title')}</SectionTitle>
              {actions.length > 0 && (
                <Row gap={2} wrap>
                  {actions.map((a) => (
                    <Button key={a.kind} variant={a.kind === 'activate' || a.kind === 'resume' ? 'primary' : a.kind === 'retire' ? 'ghost' : 'secondary'} disabled={busy || ((a.to === 'ACTIVE') && !complete)} onClick={() => void move(a)}>
                      {t(`cwa.status.${a.kind}`)}
                    </Button>
                  ))}
                </Row>
              )}
              {n.status === 'PENDING' && !complete && authority.mayManage && <Text role="caption" tone="secondary" as="p">{t('cwa.status.needsCredentials')}</Text>}
            </Stack>

            {renderCommonFields('cwa', current, setDraft, canEdit, 'edit')}
            {renderTemplates('cwa', current, setDraft, canEdit, issue?.templateIndex)}
            {canEdit && issue !== undefined && <Notice tone="danger">{refuse({ code: 'BAD_REQUEST', reasonCode: issue.code })}</Notice>}

            <Stack gap={3}>
              <SectionTitle>{t('cwa.webhook.title')}</SectionTitle>
              <Text role="caption" tone="secondary" as="p">{t('cwa.webhook.hint')}</Text>
              <Row gap={2} wrap align="baseline">
                <Text role="caption" tone="secondary">{t('cwa.webhook.path')}</Text>
                <Text mono>{n.webhookPath}</Text>
              </Row>
            </Stack>

            {!retired && (
              <Stack gap={3}>
                <SectionTitle>{t('cwa.cred.title')}</SectionTitle>
                <Text role="caption" tone="secondary" as="p">{t('cwa.cred.hint')}</Text>
                {CREDENTIAL_KIND_ORDER.map((kind) => credentialRow(d.credentials, kind))}
                {authority.mayManage && (
                  <Row gap={2}>
                    <Button variant="secondary" disabled={busy || !typed} onClick={() => void saveSecrets()}>{t('cwa.cred.save')}</Button>
                  </Row>
                )}
              </Stack>
            )}

            <Stack gap={3}>
              <SectionTitle>{t('cwa.history.title')}</SectionTitle>
              <HistoryTimeline entries={historyEntries(d)} emptyTitle={t('cwa.history.empty')} />
            </Stack>
          </DrawerStack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={closeDrawer}>{t('cwa.close')}</Button>
          {canEdit && (
            <Button variant="primary" disabled={busy || issue !== undefined || !numberHasChanges(patch)} onClick={() => void save()}>{t('cwa.save')}</Button>
          )}
        </DrawerFooter>
      </>
    );
  };

  // ---- the create drawer ---------------------------------------------------------------------------------------------

  const createDrawer = (c: NumberDraft): ReactElement => {
    const issue = createIssue(c);
    return (
      <>
        <DrawerHeader titleId="cwa-title" title={t('cwa.create.title')} onClose={closeDrawer} closeLabel={t('ui.close')} />
        <DrawerBody>
          <DrawerStack>
            {notice !== undefined && <Notice tone={notice.tone}>{notice.text}</Notice>}
            <Text role="caption" tone="secondary" as="p">{t('cwa.create.hint')}</Text>
            <Field label={t('cwa.field.country')} htmlFor="cwa-new-country" hint={t('cwa.field.country.hint')}>
              <Select id="cwa-new-country" value={c.countryCode} onChange={(e) => setCreating({ ...c, countryCode: e.target.value, marketId: '' })}>
                <option value="">{t('cwa.field.country.choose')}</option>
                {countries.map((cc) => (
                  <option key={cc} value={cc}>{countryName(cc)}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('cwa.field.phoneNumberId')} htmlFor="cwa-new-phone-id" hint={t('cwa.field.phoneNumberId.hint')}>
              <TextInput id="cwa-new-phone-id" className="tnum" value={c.phoneNumberId} autoComplete="off" inputMode="numeric" onChange={(e) => setCreating({ ...c, phoneNumberId: e.target.value })} />
            </Field>
            {renderCommonFields('cwa-new', c, setCreating, true, 'create')}
            {renderTemplates('cwa-new', c, setCreating, true, issue?.templateIndex)}
            {issue !== undefined && c.phoneNumberId !== '' && <Notice tone="danger">{refuse({ code: 'BAD_REQUEST', reasonCode: issue.code })}</Notice>}
          </DrawerStack>
        </DrawerBody>
        <DrawerFooter>
          <Button variant="ghost" onClick={closeDrawer}>{t('ui.cancel')}</Button>
          <Button variant="primary" disabled={busy || issue !== undefined} onClick={() => void create()}>{t('cwa.create.submit')}</Button>
        </DrawerFooter>
      </>
    );
  };

  const drawerOpen = detail !== undefined || creating !== undefined;

  return (
    <Stack gap={5}>
      <PageHeader title={t('cwa.title')} subtitle={t('cwa.subtitle')} actions={authority.mayManage ? <Button variant="primary" onClick={startCreate}>{t('cwa.create.open')}</Button> : undefined} />

      {!authority.mayManage && <Notice tone="neutral">{t('cwa.readOnly')}</Notice>}
      {error !== undefined && <Notice tone="danger">{error}</Notice>}
      {notice !== undefined && !drawerOpen && <Notice tone={notice.tone}>{notice.text}</Notice>}

      <FilterBar gap={4} label={t('cwa.filters')}>
        <Field label={t('cwa.filter.country')} htmlFor="cwa-country">
          <Select id="cwa-country" value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">{t('cwa.filter.country.all')}</option>
            {countries.map((cc) => (
              <option key={cc} value={cc}>{countryName(cc)}</option>
            ))}
          </Select>
        </Field>
        <Field label={t('cwa.filter.status')} htmlFor="cwa-status">
          <Select id="cwa-status" value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)}>
            <option value="">{t('cwa.filter.status.all')}</option>
            {NUMBER_STATUS_CHOICES.map((s) => (
              <option key={s} value={s}>{v('central_whatsapp.number_status', s)}</option>
            ))}
          </Select>
        </Field>
      </FilterBar>

      {numbers !== undefined && numbers.length > 0 && <Text role="caption" tone="secondary" as="p" className="tnum">{t('cwa.summary', { n: numbers.length, countries: countrySummary })}</Text>}

      <ListTable
        columns={columns}
        rows={numbers ?? []}
        rowKey={(n) => n.numberId}
        onRowClick={(n) => void openNumber(n.numberId)}
        {...(detail !== undefined ? { selectedKey: detail.number.numberId } : {})}
        loading={numbers === undefined && error === undefined}
        loadingLabel={t('ui.loading')}
        error={error !== undefined ? t('cwa.empty.failed') : undefined}
        onRetry={() => void load()}
        retryLabel={t('ui.retry')}
        empty={<EmptyState title={t('cwa.empty.title')} detail={t('cwa.empty.detail')} />}
        minWidth={980}
      />

      <Drawer open={drawerOpen} onClose={closeDrawer} labelledBy="cwa-title">
        {detail !== undefined && draft !== undefined && numberDrawer(detail, draft)}
        {creating !== undefined && createDrawer(creating)}
      </Drawer>
    </Stack>
  );
}
